import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Value from "aldoo-ra/Value"

const PlanDurationSwitcher = () => {
  const [cards] = Value("plan_cards")
  const [plans, setPlans] = useState()
  const [topPosition, setTopPosition] = useState(0)
  const [rightPosition, setRightPosition] = useState(0)

  useEffect(() => {
    if (!cards) return
    setPlans({
      monthly: cards.filter(
        (item) =>
          item.card_category === "individual" && item.product?.duration === 30
      )[0],
      yearly: cards.filter(
        (item) =>
          item.card_category === "individual" && item.product?.duration === 365
      )[0],
    })
  }, [cards])

  const [visible, setVisible] = useState(false)
  const [, setPlanDuration] = Value("plan_duration")
  const [planCategory] = Value("plan_category")

  const [isYearly, setIsYearly] = useState(false)
  const [isMobile, setIsMobile] = useState(false) // For responsive transform
  const [currentDiscountLabel, setCurrentDiscountLabel] = useState("")
  const texts = TextBundle({ id: "plan_switcher" })

  // Publish the plan duration to the PubSub channel
  useEffect(() => {
    setPlanDuration(isYearly)
    setCurrentDiscountLabel(
      isYearly ? plans?.yearly?.discount_label : plans?.monthly?.discount_label
    )
  }, [isYearly, visible, plans])

  // Check visibility based on the plan category
  useEffect(() => {
    setVisible(planCategory === "individual")
  }, [planCategory])

  // Handle viewport size for responsive styling
  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(window.innerWidth < 768) // `sm` breakpoint (768px)

      let pos = 0
      if (window.innerWidth < 768) {
        pos = 100
      } else if (window.innerWidth < 1080) {
        pos = 100
      } else if (window.innerWidth < 1280) {
        pos = 180
      } else {
        pos = 95
      }

      setTopPosition(pos)
      
      // Set right position for xl screens (1280px - 1680px)
      if (window.innerWidth >= 1280 && window.innerWidth <= 1680) {
        setRightPosition(65) // ContentDistancer width / 2
      } else {
        setRightPosition(0)
      }
    }

    // Initial check
    updateViewport()

    // Add event listener to track viewport changes
    window.addEventListener("resize", updateViewport)
    return () => {
      window.removeEventListener("resize", updateViewport)
    }
  }, [])

  // Instead of returning a divider, manage visibility with CSS transitions
  // This allows for fade in/out animations

  return (
    <button
      onClick={() => setIsYearly(!isYearly)}
      className={` fixed w-[345px] md:w-[485px] h-[44px] xl:h-[64px] flex items-center justify-between pl-4 xl:pl-5 pr-[9px] xl:pr-3 bg-theme_white rounded-full shadow-custom_card transition-all duration-300 focus:outline-none left-1/2 transform -translate-x-1/2 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        zIndex: 400,
        top: `${topPosition}px`,
        marginLeft: rightPosition ? `-${rightPosition}px` : '0',
      }}
      aria-label="Toggle plan duration"
      aria-hidden={!visible}
    >
      {/* Duration Label - Monthly */}
      <Typography variant="body_20_accent" className="text-primary">
        {texts?.monthly}
      </Typography>

      {/* Duration Toggle Button */}
      <div
        className={`relative w-[70px] md:w-[94px] xl:w-[98px] h-6 md:h-7 xl:h-8 border ${
          isYearly
            ? "bg-gradient-to-r from-primary via-primary_alt to-primary border-white/50"
            : "bg-theme_white border-primary"
        } transition-all rounded-full`}
      >
        {/* Separate wrapper for the shadow, to not interfere with the border style */}
        <div
          className={`absolute inset-0 rounded-full`}
          style={{
            boxShadow: isYearly
              ? "inset 0px 2px 2px rgba(0, 0, 0, 0.2)"
              : "none",
          }}
        />
        {/* The toggle button's head */}
        <div
          className={`absolute top-[3px] left-1  w-4 md:w-5 xl:w-6 h-4 md:h-5 xl:h-6 ${
            isYearly ? "bg-theme_white" : "bg-primary"
          } rounded-full transition-all duration-300`}
          style={{
            transform: isYearly
              ? isMobile
                ? "translateX(45px)" // Mobile-specific transform
                : "translateX(65px)" // Desktop-specific transform
              : "translateX(0)", // Default state
          }}
        />
      </div>

      {/* Duration Label - Yearly */}
      <Typography variant="body_20_accent" className="text-primary select-none">
        {texts?.yearly}
      </Typography>

      {/* Discount Label */}
      <div className="w-[120px] md:w-[170px] h-[30px] xl:h-[42px] flex items-center justify-center bg-primary text-white rounded-full select-none">
        <Typography variant="body_20_light">{currentDiscountLabel}</Typography>
      </div>
    </button>
  )
}

const AldooComponent = {
  id: 'PlanDurationSwitcher',
  component: PlanDurationSwitcher,
  input: {},
  meta: {
    name: 'Plan Duration Switcher',
    description: 'Toggle between monthly and yearly subscription plans',
    category: 'Marketing',
  },
}

export { AldooComponent }
export default PlanDurationSwitcher